import { Form, Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignSaveAndGenerate } from "../../hooks/useCampaignSaveAndGenerate";
import { useCampaignSaveMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignCreateJson from "./campaign-terminals-create.json";

export const CampaignTerminalsCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const formDefinition = campaignCreateJson as FormJson;
    const [form] = Form.useForm();

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const { saveAndGenerate, isSaveInProgress } = useCampaignSaveAndGenerate(useCampaignSaveMutation);
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CAMPAIGNS}`);

    // Get default values from form definition
    useEffect(() => {
        if (formDefinition?.formConfiguration) {
            const initialValues: CampaignEntity = {} as CampaignEntity;

            formDefinition.formConfiguration.tabs.forEach((tab) => {
                tab.fields.forEach((field) => {
                    const defaultValue = field.attributes?.defaultValue;
                    if (defaultValue !== undefined) {
                        (initialValues as Record<string, unknown>)[field.name] = defaultValue;
                    }
                });
            });

            setFormData(initialValues as CampaignEntity);
        }
    }, [formDefinition]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // Get fresh values from form instance to capture CSV imports and other changes
            // that may not have propagated to formData due to React batching
            const freshValues = form.getFieldsValue();
            const dataToSave = { ...formData, ...freshValues };

            const result = await saveAndGenerate(dataToSave, formAction);

            const hasError = "error" in result;
            if (!hasError) {
                const successText =
                    formAction === FORM_ACTION.GENERATE
                        ? i18n.t("success.campaign.generate.successfully", { 0: result.data.id })
                        : i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Campaign" });
                completeSubmit(
                    successText,
                    `${BASE_PATH}/${ResourceName.CAMPAIGNS}/${ResourceVariant.TERMINALS}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveAndGenerate, formData, form, formAction, completeSubmit],
    );

    return (
        <Spin spinning={isSaveInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignTerminalsCreateForm"
                    customFormInstance={form}
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
